import { c as _c } from "react/compiler-runtime";
import { IcoClose16 } from '@onefootprint/icons';
import { cx } from 'class-variance-authority';
import type React from 'react';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton } from '../..';
import useShortcut from '../../hooks/use-shortcut';
import Dialog from '../dialog';
import Footer from './components/footer';
import type { DialogPageButton } from './dialog-page.types';
export type DialogPageProps = {
  documentTitle?: string;
  title: string | React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  primaryButton?: DialogPageButton;
  secondaryButton?: DialogPageButton;
  noPadding?: boolean;
  noScroll?: boolean;
  showHeader?: boolean;
  confirmBeforeClose?: {
    title: string;
    primaryButtonLabel?: string;
    secondaryButtonLabel?: string;
    description: string;
  };
  preventEscapeKeyDown?: boolean;
  bannerComponent?: React.ReactNode;
  hasWarningOutline?: boolean;
};
const DialogPage = forwardRef<HTMLDivElement, DialogPageProps>((t0, ref) => {
  const $ = _c(53);
  const {
    documentTitle,
    title,
    onClose,
    children,
    primaryButton,
    secondaryButton,
    showHeader: t1,
    noPadding: t2,
    noScroll: t3,
    confirmBeforeClose,
    preventEscapeKeyDown: t4,
    bannerComponent,
    hasWarningOutline: t5
  } = t0;
  const showHeader = t1 === undefined ? true : t1;
  const noPadding = t2 === undefined ? false : t2;
  const noScroll = t3 === undefined ? false : t3;
  const preventEscapeKeyDown = t4 === undefined ? false : t4;
  const hasWarningOutline = t5 === undefined ? false : t5;
  let t6;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      keyPrefix: "components.dialog-page"
    };
    $[0] = t6;
  } else {
    t6 = $[0];
  }
  const {
    t
  } = useTranslation("ui", t6);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const contentRef = useRef(null);
  const isClosingConfirmRef = useRef(false);
  const hasFooter = primaryButton || secondaryButton;
  let t7;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      if (!isClosingConfirmRef.current) {
        setIsConfirmOpen(true);
      }
    };
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  const openConfirmDialog = t7;
  let t8;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      isClosingConfirmRef.current = true;
      setIsConfirmOpen(false);
      setTimeout(() => {
        isClosingConfirmRef.current = false;
      }, 150);
    };
    $[2] = t8;
  } else {
    t8 = $[2];
  }
  const closeConfirmDialog = t8;
  let t9;
  if ($[3] !== onClose) {
    t9 = () => {
      closeConfirmDialog();
      onClose();
    };
    $[3] = onClose;
    $[4] = t9;
  } else {
    t9 = $[4];
  }
  const handleConfirm = t9;
  let t10;
  if ($[5] !== confirmBeforeClose || $[6] !== onClose) {
    t10 = () => {
      confirmBeforeClose ? openConfirmDialog() : onClose();
    };
    $[5] = confirmBeforeClose;
    $[6] = onClose;
    $[7] = t10;
  } else {
    t10 = $[7];
  }
  const handleClose = t10;
  const handleEscapeKey = preventEscapeKeyDown || !showHeader ? undefined : handleClose;
  let t11;
  if ($[8] !== handleEscapeKey || $[9] !== preventEscapeKeyDown) {
    t11 = {
      onEscKeyDown: handleEscapeKey,
      disabled: preventEscapeKeyDown
    };
    $[8] = handleEscapeKey;
    $[9] = preventEscapeKeyDown;
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  useShortcut(t11);
  let t12;
  let t13;
  if ($[11] !== documentTitle) {
    t12 = () => {
      if (documentTitle) {
        const originalTitle = document.title;
        document.title = documentTitle;
        return () => {
          document.title = originalTitle;
        };
      }
    };
    t13 = [documentTitle];
    $[11] = documentTitle;
    $[12] = t12;
    $[13] = t13;
  } else {
    t12 = $[12];
    t13 = $[13];
  }
  useEffect(t12, t13);
  let t14;
  if ($[14] !== hasWarningOutline) {
    t14 = cx("flex fixed inset-0 flex-col w-screen h-dvh z-dialog bg-primary md:h-screen", {
      "border-[var(--border-color-error)] border-[1.5px] rounded-3xl": hasWarningOutline
    });
    $[14] = hasWarningOutline;
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  let t15;
  if ($[16] !== hasWarningOutline) {
    t15 = cx("flex flex-col flex-grow min-h-full overflow-auto relative", {
      "rounded-xl": hasWarningOutline
    });
    $[16] = hasWarningOutline;
    $[17] = t15;
  } else {
    t15 = $[17];
  }
  let t16;
  if ($[18] !== bannerComponent || $[19] !== handleClose || $[20] !== showHeader || $[21] !== t || $[22] !== title) {
    t16 = showHeader && <header className={cx("w-full h-[44px] grid grid-cols-[32px_minmax(0,1fr)_32px] items-center border-b border-tertiary bg-primary px-3 flex-shrink-0 z-10", "md:grid-cols-[minmax(100px,1fr)_auto_minmax(100px,1fr)]", {
      border: !!bannerComponent
    })}><IconButton icon={IcoClose16} aria-label={t("header-icon.aria-label-default")} onClick={handleClose} /><div className="text-center text-label-2 max-md:truncate">{title}</div></header>;
    $[18] = bannerComponent;
    $[19] = handleClose;
    $[20] = showHeader;
    $[21] = t;
    $[22] = title;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  const t17 = !noPadding;
  let t18;
  if ($[24] !== noPadding || $[25] !== noScroll || $[26] !== t17) {
    t18 = cx("overflow-auto flex-1 h-full", {
      "p-0": noPadding,
      "p-6": t17,
      "overflow-hidden": noScroll
    });
    $[24] = noPadding;
    $[25] = noScroll;
    $[26] = t17;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  if ($[28] !== children || $[29] !== t18) {
    t19 = <div ref={contentRef} className={t18}>{children}</div>;
    $[28] = children;
    $[29] = t18;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[31] !== hasFooter || $[32] !== primaryButton || $[33] !== secondaryButton) {
    t20 = hasFooter && <Footer primaryButton={primaryButton} secondaryButton={secondaryButton} />;
    $[31] = hasFooter;
    $[32] = primaryButton;
    $[33] = secondaryButton;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  let t21;
  if ($[35] !== bannerComponent || $[36] !== t15 || $[37] !== t16 || $[38] !== t19 || $[39] !== t20) {
    t21 = <div className={t15}>{bannerComponent}{t16}{t19}{t20}</div>;
    $[35] = bannerComponent;
    $[36] = t15;
    $[37] = t16;
    $[38] = t19;
    $[39] = t20;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== ref || $[42] !== t14 || $[43] !== t21) {
    t22 = <div aria-label="dialog-page" ref={ref} className={t14}>{t21}</div>;
    $[41] = ref;
    $[42] = t14;
    $[43] = t21;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== confirmBeforeClose || $[46] !== handleConfirm || $[47] !== isConfirmOpen || $[48] !== t) {
    t23 = confirmBeforeClose && isConfirmOpen && <Dialog open={isConfirmOpen} onClose={closeConfirmDialog} title={confirmBeforeClose.title} isConfirmation={true} size="compact" onEscapeKeyDown={isConfirmOpen ? closeConfirmDialog : undefined} primaryButton={{
      variant: "destructive",
      label: confirmBeforeClose.primaryButtonLabel || t("confirm-before-close.confirm"),
      onClick: handleConfirm
    }} secondaryButton={{
      label: confirmBeforeClose.secondaryButtonLabel || t("confirm-before-close.cancel"),
      onClick: closeConfirmDialog
    }}>{confirmBeforeClose.description}</Dialog>;
    $[45] = confirmBeforeClose;
    $[46] = handleConfirm;
    $[47] = isConfirmOpen;
    $[48] = t;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== t22 || $[51] !== t23) {
    t24 = <>{t22}{t23}</>;
    $[50] = t22;
    $[51] = t23;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  return t24;
});
export default DialogPage;